import { ChangeDetectorRef, Component, ElementRef, Injectable, Input, OnDestroy, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Observable, concatMap, map, of, share, tap, throwError } from 'rxjs';
import { Convert } from 'types';
import { AppContext } from '../../base/control/context';
import { ImageBlob } from '../../base/model/features-decl';
import { ValidationService } from '../../base/validation/validation.service';
import { HWServiceConfig } from '../../service/hw-service-config';
import { SignatureScannerConfiguration, SignatureScannerService } from '../../service/signature-scanner.service';

export interface WacomSignWideScreenBackgroundParams {
  rusSurname: string;
  rusLastname: string;
  rusName: string;
  latName: string;
  birthplace: string;
  sex: string;
  birthdate: string;
  photo?: string;
}

@Injectable()
export abstract class SignatureScannerServiceFactory {
  abstract create(config: HWServiceConfig): SignatureScannerService;
}

@Component({
  selector: 'i-wacom-sign',
  templateUrl: './wacom-sign.component.html',
  styleUrls: ['./wacom-sign.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => WacomSignComponent), multi: true },
    { provide: SignatureScannerServiceFactory, useValue: { create: (c: HWServiceConfig) => new SignatureScannerService(c) } },
  ],
})
export class WacomSignComponent implements OnDestroy, ControlValueAccessor {
  private _signatureScannerService: SignatureScannerService;
  private _clearImageSrc = 'assets/images/clearsign.bmp';
  private _before15ImageSrc = 'assets/images/before15sign.bmp';

  @ViewChild('signatureImage')
  signatureImageElement: ElementRef<HTMLImageElement>;
  @ViewChild('videoStream')
  videoStreamElement: ElementRef<HTMLImageElement>;
  @Input() flags: ImageBlob;
  @Input() width = 709;
  @Input() height = 142;

  CurrentState: EnWacomSignState = EnWacomSignState.Ready;

  constructor(
    private _signatureScannerServiceF: SignatureScannerServiceFactory,
    private validationService: ValidationService,
    private _appContext: AppContext,
    private _config: HWServiceConfig,
    private _cdr: ChangeDetectorRef,
  ) {
    this._signatureScannerService = this._signatureScannerServiceF.create(this._config);
  }

  ngOnDestroy() {
    this.closeService();
  }

  SignatureImage: SafeResourceUrl;
  VideoStream: SafeResourceUrl;

  onSignatureImageLoad() {
    let c = document.createElement('canvas');
    c.width = this.signatureImageElement.nativeElement.naturalWidth;
    c.height = this.signatureImageElement.nativeElement.naturalHeight;
    c.getContext('2d')?.drawImage(this.signatureImageElement.nativeElement, 0, 0);
    const dataUrl = c.toDataURL();
    this.onChange(dataUrl);
    if (this.SignatureImage === this._clearImageSrc) {
      this.flags.scanned = false;
    } else {
      this.flags.scanned = true;
    }
    this.validationService.validate();
  }

  private onChange: (arg: any) => void;

  registerOnChange(onChange: (arg: any) => void) {
    this.onChange = onChange;
  }

  registerOnTouched() {}

  setDisabledState() {}

  writeValue(obj: any) {
    if (typeof obj === 'string') {
      if (!!obj) {
        this.SignatureImage = obj;
        this._cdr.detectChanges();
      } else {
        this.onClear();
      }
    }
  }

  isInReadyState() {
    return this.CurrentState === EnWacomSignState.Ready;
  }

  isInReadingState() {
    return this.CurrentState === EnWacomSignState.Reading;
  }

  private closeService(): void {
    this._signatureScannerService
      .tryClose()
      .pipe(concatMap(() => this._signatureScannerService.disconnect()))
      .subscribe();
  }

  onStart() {
    if (!this.isInReadyState()) {
      return;
    }
    this.VideoStream = 'assets/images/clearsign.bmp';
    this._cdr.detectChanges();
    const signatureScannerRead = this._signatureScannerService.read();
    this._signatureScannerService
      .ensureConnected()
      .pipe(
        concatMap(() => this._signatureScannerService.listDevices()),
        map((devices) => devices[0]),
        concatMap((device) => (device ? of(device) : throwError(() => new Error('Не найдено ни одного устройства')))),
        concatMap((device) => this._signatureScannerService.open(device.id)),
      )
      .subscribe({
        complete: () => {
          const beforeStream = this.beforeStream().pipe(share());
          beforeStream.pipe(concatMap(() => signatureScannerRead.streamURL)).subscribe({
            next: (x) => {
              this.VideoStream = x;
              this.CurrentState = EnWacomSignState.Reading;
              this._cdr.detectChanges();
            },
          });
          beforeStream
            .pipe(
              concatMap(() => signatureScannerRead.result),
              concatMap((r) => this.convertScannerResultToDataURL$(r)),
            )
            .subscribe({
              next: (x) => {
                this.SignatureImage = x;
                this.closeService();
                this.CurrentState = EnWacomSignState.Ready;
                this._cdr.detectChanges();
              },
              error: (err) => {
                this.closeService();
                this.CurrentState = EnWacomSignState.Ready;
                this._cdr.detectChanges();
                this._appContext.getMessageSink()('ERROR', 'wacom.sign.error.get.sign.result');
              },
              complete: () => {},
            });
        },
        error: (err) => this._appContext.getMessageSink()('ERROR', 'wacom.sign.error.stop.read.sign'),
      });
  }

  onStop() {
    if (!this.isInReadingState()) {
      return;
    }
    this._signatureScannerService.stopReading().subscribe({
      next: (_) => {},
      error: (e) => {
        this._appContext.getMessageSink()('ERROR', 'wacom.sign.error.stop.read.sign');
        this.closeService();
        this.CurrentState = EnWacomSignState.Ready;
        this._cdr.detectChanges();
      },
      complete: () => {
        this.CurrentState = EnWacomSignState.Ready;
        this._cdr.detectChanges();
      },
    });
  }

  onDash() {
    if (!this.isInReadyState()) {
      return;
    }
    this.SignatureImage = this._before15ImageSrc;
    this._cdr.detectChanges();
  }

  onClear() {
    if (!this.isInReadyState()) {
      return;
    }
    this.SignatureImage = this._clearImageSrc;
    this._cdr.detectChanges();
  }

  protected beforeStream(): Observable<void> {
    return this.resetConfiguration();
  }

  protected resetConfiguration(): Observable<void> {
    return this._signatureScannerService.configure({});
  }

  protected openInfoScreenVersion(params: WacomSignWideScreenBackgroundParams): Observable<void> {
    return this._signatureScannerService.getCapabilities().pipe(
      concatMap((capabilities) =>
        this.drawInfoScreenBackground(
          {
            width: capabilities['screen-width'],
            height: capabilities['screen-height'],
          },
          params,
        ),
      ),
      concatMap((conf) => this._signatureScannerService.configure(conf)),
    );
  }

  protected openFullScreenVersion(): Observable<void> {
    return this._signatureScannerService.getCapabilities().pipe(
      concatMap((capabilities) =>
        this.drawFullScreenBackground({
          width: capabilities['screen-width'],
          height: capabilities['screen-height'],
        }),
      ),
      concatMap((conf) => this._signatureScannerService.configure(conf)),
    );
  }

  protected drawInfoScreenBackground(
    size: { width: number; height: number },
    params: WacomSignWideScreenBackgroundParams,
  ): Observable<SignatureScannerConfiguration> {
    const maxStringLength = 28;
    const limitString = (str: string) => str.substring(0, maxStringLength);

    const getZones = (origWidth: number, origHeight: number): SignatureScannerConfiguration['zones'] => {
      const resizeX = (value: number) => CanvasDrawingHelper.remap(value, 0, origWidth, 0, size.width);
      const resizeY = (value: number) => CanvasDrawingHelper.remap(value, 0, origHeight, 0, size.height);
      // Позиции и размеры захардкожены из "assets/images/signature.bmp"
      return [
        // clear btn
        {
          x: resizeX(29),
          y: resizeY(363),
          width: resizeX(115),
          height: resizeY(89),
          type: 'clear',
        },
        // ok btn
        {
          x: resizeX(157),
          y: resizeY(363),
          width: resizeX(115),
          height: resizeY(89),
          type: 'stop',
        },
        // signature rect
        {
          x: resizeX(311),
          y: resizeY(256),
          width: resizeX(459),
          height: resizeY(195),
          type: 'signature',
        },
      ];
    };

    // Следующий код взят из
    // ARM1\ScanSignatureDlg.cpp#235

    const drawBackground = (context: CanvasRenderingContext2D, backgroundImage: HTMLImageElement) => {
      // Рисуем фоновое изображение
      context.drawImage(backgroundImage, 0, 0);
    };

    const drawTextData = (context: CanvasRenderingContext2D) => {
      // Мы не можем указать отрицательный размер шрифта, поэтому добавляем отступ
      const offsetY = 24;
      // Рисуем текст
      context.fillText(limitString(params.rusLastname), 311, 25 + offsetY);
      context.fillText(limitString(params.rusName + ' ' + params.rusSurname), 311, 65 + offsetY);
      context.fillText(limitString(params.latName), 311, 105 + offsetY);
      context.fillText(limitString(params.birthplace), 311, 145 + offsetY);
      context.fillText(params.sex, 311, 185 + offsetY);
      context.fillText(params.birthdate, 452, 185 + offsetY);
    };

    const drawPhoto = (context: CanvasRenderingContext2D): Observable<CanvasRenderingContext2D> => {
      if (!params.photo) {
        return of(context);
      } else {
        return CanvasDrawingHelper.createImage$(Convert.wrapBase64ToDataUrl(params.photo)).pipe(
          tap((photoImage) => {
            context.drawImage(photoImage, 30, 31, 240, 321);
          }),
          map(() => context),
        );
      }
    };

    return CanvasDrawingHelper.createImage$('assets/images/signature.bmp').pipe(
      concatMap((backgroundImage) =>
        CanvasDrawingHelper.createCanvas$(backgroundImage.naturalWidth, backgroundImage.naturalHeight).pipe(
          concatMap((canvas) =>
            CanvasDrawingHelper.createCanvasContext$(canvas).pipe(
              tap((context) => drawBackground(context, backgroundImage)),
              tap((context) => drawTextData(context)),
              concatMap((context) => drawPhoto(context)),
              concatMap(() =>
                CanvasDrawingHelper.createCanvas$(size.width, size.height).pipe(
                  concatMap((resizedCanvas) =>
                    CanvasDrawingHelper.createCanvasContext$(resizedCanvas).pipe(
                      tap((resizedContext) => {
                        resizedContext.drawImage(canvas, 0, 0, size.width, size.height);
                      }),
                      map(() => Convert.unwrapDataUrlToBase64(canvas.toDataURL())),
                    ),
                  ),
                ),
              ),
              map((image) => ({ image, zones: getZones(backgroundImage.width, backgroundImage.height) })),
            ),
          ),
        ),
      ),
    );
  }

  protected drawFullScreenBackground(size: { width: number; height: number }): Observable<SignatureScannerConfiguration> {
    const drawBackground = (context: CanvasRenderingContext2D, backgroundImage: HTMLImageElement) => {
      // Рисуем фоновое изображение
      context.drawImage(backgroundImage, 0, 0);
    };

    const getZones = (origWidth: number, origHeight: number): SignatureScannerConfiguration['zones'] => {
      const resizeX = (value: number) => CanvasDrawingHelper.remap(value, 0, origWidth, 0, size.width);
      const resizeY = (value: number) => CanvasDrawingHelper.remap(value, 0, origHeight, 0, size.height);
      // Позиции и размеры захардкожены из "assets/images/allScreenSignature.bmp"
      return [
        // clear btn
        {
          x: resizeX(29),
          y: resizeY(363),
          width: resizeX(115),
          height: resizeY(89),
          type: 'clear',
        },
        // ok btn
        {
          x: resizeX(642),
          y: resizeY(363),
          width: resizeX(115),
          height: resizeY(89),
          type: 'stop',
        },
        // signature rect
        {
          x: resizeX(65),
          y: resizeY(55),
          width: resizeX(666),
          height: resizeY(283),
          type: 'signature',
        },
      ];
    };

    return CanvasDrawingHelper.createImage$('assets/images/allScreenSignature.bmp').pipe(
      concatMap((backgroundImage) =>
        CanvasDrawingHelper.createCanvas$(backgroundImage.naturalWidth, backgroundImage.naturalHeight).pipe(
          concatMap((canvas) =>
            CanvasDrawingHelper.createCanvasContext$(canvas).pipe(
              tap((context) => drawBackground(context, backgroundImage)),
              concatMap(() =>
                CanvasDrawingHelper.createCanvas$(size.width, size.height).pipe(
                  concatMap((resizedCanvas) =>
                    CanvasDrawingHelper.createCanvasContext$(resizedCanvas).pipe(
                      tap((resizedContext) => {
                        resizedContext.drawImage(canvas, 0, 0, size.width, size.height);
                      }),
                      map(() => Convert.unwrapDataUrlToBase64(canvas.toDataURL())),
                    ),
                  ),
                ),
              ),
              map((image) => ({ image, zones: getZones(backgroundImage.width, backgroundImage.height) })),
            ),
          ),
        ),
      ),
    );
  }

  private convertScannerResultToDataURL$(signature: Uint8Array): Observable<string> {
    return new Observable((observer) => {
      const signatureImage = new Image();
      signatureImage.src = Convert.wrapBase64ToDataUrl(Convert.toBase64(signature));
      signatureImage.onload = () => {
        const scaleWidth = this.width / signatureImage.width;
        const scaleHeight = this.height / signatureImage.height;
        const scale = Math.min(scaleWidth, scaleHeight);
        const scaledWidth = signatureImage.width * scale;
        const scaledHeight = signatureImage.height * scale;
        const canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        const context = canvas.getContext('2d')!;
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(signatureImage, (this.width - scaledWidth) / 2, (this.height - scaledHeight) / 2, scaledWidth, scaledHeight);
        observer.next(canvas.toDataURL());
        observer.complete();
      };
    });
  }
}

export enum EnWacomSignState {
  Ready = 0,
  Reading = 1,
}

class CanvasDrawingHelper {
  static createImage$(src: string) {
    return new Observable<HTMLImageElement>((observer) => {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        observer.next(image);
        observer.complete();
      };
    });
  }

  static createCanvas$(width: number, height: number) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return of(canvas);
  }

  static createCanvasContext$(canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d')!;
    context.fillStyle = 'black';
    context.font = 'bold 24px Arial';
    return of(context);
  }

  static remap(value: number, low1: number, high1: number, low2: number, high2: number) {
    return low2 + ((high2 - low2) * (value - low1)) / (high1 - low1);
  }
}
